/**
 * Shared UI component for UI Form Action Footer.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { UiButtonComponent } from '../ui-button/ui-button.component';
import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';

/**
 * COMPONENT: Ui Form Action Footer
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Component({
  selector: 'ui-form-action-footer',
  standalone: true,
  imports: [UiButtonComponent, UiLabelTranslatePipe],
  templateUrl: './ui-form-action-footer.component.html',
  styleUrl: './ui-form-action-footer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiFormActionFooterComponent {
  /** PARENT-CHILD CONTRACT: showBack documents the input state boundary used by this file. */
  readonly showBack = input(false);
  /** PARENT-CHILD CONTRACT: showSave documents the input state boundary used by this file. */
  readonly showSave = input(true);
  /** PARENT-CHILD CONTRACT: showNext documents the input state boundary used by this file. */
  readonly showNext = input(false);
  /** PARENT-CHILD CONTRACT: isSaving documents the input state boundary used by this file. */
  readonly isSaving = input(false);
  /** PARENT-CHILD CONTRACT: disableBack documents the input state boundary used by this file. */
  readonly disableBack = input(false);
  /** PARENT-CHILD CONTRACT: disableSave documents the input state boundary used by this file. */
  readonly disableSave = input(false);
  /** PARENT-CHILD CONTRACT: disableNext documents the input state boundary used by this file. */
  readonly disableNext = input(false);
  /** PARENT-CHILD CONTRACT: backLabel documents the input state boundary used by this file. */
  readonly backLabel = input('Back');
  /** PARENT-CHILD CONTRACT: saveLabel documents the input state boundary used by this file. */
  readonly saveLabel = input('Save');
  /** PARENT-CHILD CONTRACT: nextLabel documents the input state boundary used by this file. */
  readonly nextLabel = input('Next');
  readonly backClicked = output<void>();
  readonly saveClicked = output<void>();
  readonly nextClicked = output<void>();
  /** STATE OWNER: usesSplitLayout documents the computed state boundary used by this file. */
  readonly usesSplitLayout = computed(() => this.showBack() && this.showSave() && this.showNext());

  /**
   * BUSINESS LOGIC: onBackClicked
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onBackClicked(): void {
    this.backClicked.emit();
  }

  /**
   * BUSINESS LOGIC: onSaveClicked
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onSaveClicked(): void {
    this.saveClicked.emit();
  }

  /**
   * BUSINESS LOGIC: onNextClicked
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onNextClicked(): void {
    this.nextClicked.emit();
  }
}
